import {
    inject,
    Injectable
} from "@angular/core";

import {
    EntityManagerService,
    EnvironmentInfoService
} from "src/app/core";

import {
    Printers
} from "tilby-models";

type OtherPrinterConfig = {
    id: number,
    inline: boolean
}

export type DocumentPrinterConfiguration = {
    split_stubs_by_category?: boolean
    print_other_categories?: boolean
}

export type OrderPrinterConfiguration = {
    add_prices_to_order?: false
    codepage_mapping?: string
    footer_printer_categories?: number
    footer_order_number?: number
    footer_operator_name?: number
    footer_print_type?: number
    footer_date?: number
    footer_time?: number
    footer_room_name?: number
    footer_table_name?: number
    footer_covers?: number
    footer_delivery_info?: number
    footer_order_name?: number
    footer_customer_info?: number
    footer_order_notes?: number
    footer_total_pieces?: number
    footer_final_amount?: number
    from_cashregisters_only?: boolean
    header_printer_categories?: number
    header_order_number?: number
    header_operator_name?: number
    header_print_type?: number
    header_date?: number
    header_time?: number
    header_room_name?: number
    header_table_name?: number
    header_covers?: number
    header_delivery_info?: number
    header_order_name?: number
    header_customer_info?: number
    header_order_notes?: number
    header_total_pieces?: number
    header_final_amount?: number
    item_font_size?: number
    item_spacing?: number
    item_hide_border?: boolean
    item_hide_brackets?: boolean
    items_align?: string
    items_group_by?: 'exit' | 'category_id' | 'exit_and_category',
    merge_same_items?: boolean,
    line_spacing?: number
    split_orders_by_exit?: boolean
    variation_font_size?: number
    progressive_prefix?: string
    other_printers?: OtherPrinterConfig[]
    other_printers_font_size?: number,
    cashregisters?: number[]
    rooms?: (number | 'no_rooms' | 'take_away' | 'delivery')[]
}

export type OrderPrinterWithConfig = Omit<Printers, 'configuration'> & {
    configuration: OrderPrinterConfiguration
}

export type DocumentPrinterWithConfig = Omit<Printers, 'configuration'> & {
    configuration: DocumentPrinterConfiguration
}

@Injectable({
    providedIn: 'root'
})
export class PrinterUtilsService {
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly entityManagerService = inject(EntityManagerService);

    /**
     * Parses the configuration of a printer from a JSON string to an object and handles backward compatibility.
     * @param printer The printer object to process.
     * @returns A PrinterWithConfig object with the parsed and normalized configuration.
     */
    public getPrinterWithConfig(printer: Printers): OrderPrinterWithConfig | DocumentPrinterWithConfig {
        try {
            const configuration = JSON.parse(printer.configuration || '{}');

            if (!Array.isArray(configuration.other_printers)) {
                configuration.other_printers = [];
            }

            // Backward compatibility of other_printers
            if (configuration.other_printers.length > 0 && typeof configuration.other_printers[0] === 'number') {
                configuration.other_printers = configuration.other_printers.map((p: number) => {
                    return {
                        id: p,
                        inline: false,
                    };
                });
            }

            if (!Array.isArray(configuration.cashregisters)) {
                configuration.cashregisters = [];
            }

            if (!Array.isArray(configuration.rooms)) {
                configuration.rooms = [];
            }

            return {
                ...printer,
                configuration,
            };
        } catch (err) {
            return {
                ...printer,
                configuration: {
                    other_printers: [],
                    cashregisters: [],
                    rooms: [],
                },
            };
        }
    }

    /**
     * Retrieves a filtered list of order printers based on various criteria.
     * @param printers An optional array of Printers to filter. If not provided, printers will be fetched.
     * @param options An optional object containing filtering options such as `isAutoPrint` and `sourceCashregister`.
     * @param options.isAutoPrint if true, the order printer is requested by a document print instead of an operator
     * @param options.sourceCashregister The ID of the document printer from which the order print request originated.
     * @returns A Promise that resolves to an array of filtered Printers.
     */
    public async getOrdersPrinters(printers?: Printers[], options?: { isAutoPrint?: boolean, sourceCashregister?: number }) {
        const { isAutoPrint, sourceCashregister } = options || {}

        if (!printers) {
            printers = await this.entityManagerService.printers.fetchCollectionOffline();
        }

        let ordersPrinters = printers.filter((printer) => ['nonfiscal', 'kds'].includes(printer.type));

        // Remove escpos printers if we are in an environment that doesn't support TCP sockets
        if (!this.environmentInfoService.canUseTcpSockets()) {
            ordersPrinters = ordersPrinters.filter((printer) => printer.driver !== 'escpos');
        }

        if (!isAutoPrint) {
            // If this is a manual print, remove printers that allow only cashregisters prints
            ordersPrinters = ordersPrinters.filter((printer) => {
                const { configuration } = <OrderPrinterWithConfig>this.getPrinterWithConfig(printer);

                return !configuration.from_cashregisters_only;
            });
        } else if (sourceCashregister) {
            // If this is an auto print and we have a source cashregister specified, only use order printers that allow this cashregister
            ordersPrinters = ordersPrinters.filter((printer) => {
                const { configuration } = <OrderPrinterWithConfig>this.getPrinterWithConfig(printer);

                return (configuration.cashregisters || []).includes(sourceCashregister);
            });
        }

        return ordersPrinters;
    };

    /**
     * Checks if price printing is enabled for any of the order printers.
     *
     * @returns A Promise that resolves to `true` if at least one order printer has `add_prices_to_order` enabled, `false` otherwise.
     */
    public async isOrderPricePrintEnabled () {
        const ordersPrinters = await this.getOrdersPrinters().then(ps => ps.map(p => <OrderPrinterWithConfig>this.getPrinterWithConfig(p)));

        return ordersPrinters.some(p => p.configuration.add_prices_to_order);
    }
}